import "semantic-ui-css/semantic.min.css";
import React, { useState, useEffect, useRef, useMemo } from "react";
import "./GlobalSearch.css";
import { useSite } from "../../context/siteContext";
import {
	Box,
	CircularProgress,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { ASSETS_BASE_URL, SERVER_BASE_URL } from "../../config";
import { useTranslation } from "react-i18next";
import { searchProducts } from "../../services/globalSearchService";
import { useAuth } from "../../context/authContext";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import useAccessiblePages from "../../hooks/useAccessiblePages";
import DocumentViewModal from "../DocumentViewModal/DocumentViewModal";
import DocCardImage from "../DocCardImage/DocCardImage";
import ReplacementPartNotice from "../ReplacementPartNotice/ReplacementPartNotice";
import { buildExternalLoginUrl } from "../../utilities/externalDocs";
import { ROUTES } from "../../constants";
import { canAccessOrderHistory } from "../../utilities/pageAccess";
import { trackEvent } from "../../utilities/analyticsService";

const getMatchingPages = (pages, searchTerm, partsOnly) => {
	if (partsOnly || !searchTerm || searchTerm.length < 2) return [];
	const term = searchTerm.toLowerCase();
	return pages.filter((page) => {
		const nameMatch = (page.name || "").toLowerCase().includes(term);
		const searchTermsMatch = (page.searchTerms || []).some((st) =>
			st.toLowerCase().includes(term)
		);
		return nameMatch || searchTermsMatch;
	});
};

const parseJsonArray = (value) => {
	if (Array.isArray(value)) return value;
	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}
	return [];
};

const parseNumberArray = (value) => {
	if (Array.isArray(value)) {
		return value.map((val) => Number(val)).filter((num) => Number.isFinite(num));
	}
	if (typeof value === "string") {
		const trimmed = value.replace(/^\{|\}$/g, "");
		if (!trimmed) return [];
		return trimmed
			.split(",")
			.map((val) => Number(val.trim()))
			.filter((num) => Number.isFinite(num));
	}
	return [];
};

const buildDocSubtitle = (doc, t) => {
	const machines = parseJsonArray(doc?.machine_details).map(
		(machine) => machine?.machine_number || machine?.machine_name
	).filter(Boolean);
	const categories = parseJsonArray(doc?.categories)
		.map((cat) => cat?.category_name)
		.filter(Boolean);

	const summarize = (values) => {
		const limit = 2;
		if (values.length <= limit) return values.join(", ");
		return `${values.slice(0, limit).join(", ")} +${values.length - limit} more`;
	};

	const machineLabel = t("UI_TEXT.MACHINES") || "Machines";
	const rawCategoryLabel = t("UI_TEXT.CATEGORIES");
	const categoryLabel =
		rawCategoryLabel && rawCategoryLabel !== "UI_TEXT.CATEGORIES"
			? rawCategoryLabel
			: "Categories";

	const parts = [];
	if (machines.length) {
		parts.push(`${machineLabel}: ${summarize(machines)}`);
	}
	if (categories.length) {
		parts.push(`${categoryLabel}: ${summarize(categories)}`);
	}
	return parts.join(" • ");
};

const getDocumentLink = (item) => {
	const type = String(item?.attachment_type || "").toLowerCase();
	if (type === "url" && item?.attachment) {
		return item.attachment;
	}
	if (type === "file" && item?.attachment) {
		return `${SERVER_BASE_URL}/${item.attachment}`;
	}
	return null;
};

const getExternalDocumentsLoginUrl = ({ enabled, templateUrl, selectedSiteData }) => {
	if (!enabled || !templateUrl) return "";
	const username = selectedSiteData?.amf_method_username_ln;
	const password = selectedSiteData?.amf_method_password_ln;
	return buildExternalLoginUrl({
		templateUrl,
		username,
		password,
	});
};

const resolveAssetImage = (raw, stockImageUrl) => {
	if (raw) {
		if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
		const path = raw.startsWith("/") ? raw : `/${raw}`;
		return `${ASSETS_BASE_URL}${path}`;
	}
	return stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
};

const ResultText = ({ primary, secondary }) => (
	<div className="result-info">
		<Tooltip title={primary || ""} placement="top" arrow>
			<Typography className="result-title">{primary}</Typography>
		</Tooltip>
		{secondary ? (
			<Tooltip title={secondary} placement="top" arrow>
				<div className="category-text">{secondary}</div>
			</Tooltip>
		) : null}
	</div>
);

const getResultCategory = (item) => {
	const category = String(item?.category || "").trim().toLowerCase();
	if (category === "products" || category === "product") return "Products";
	if (category === "machines" || category === "machine") return "Machines";
	if (category === "orders" || category === "order") return "Orders";
	if (category === "documents" || category === "document") return "Documents";
	if (category === "pages" || category === "page") return "Pages";
	return item?.category || "";
};

const getSearchResultKey = (item) => {
	const category = getResultCategory(item);
	if (category === "Orders") {
		return `Orders:${item?.order_id ?? item?.product_id ?? item?.order_number ?? item?.name ?? ""}`;
	}
	if (category === "Products") {
		return `Products:${item?.product_id ?? ""}:${item?.machine_id ?? ""}`;
	}
	if (category === "Machines") {
		return `Machines:${item?.machine_id ?? item?.product_id ?? item?.machine_number ?? ""}`;
	}
	if (category === "Documents") {
		return `Documents:${item?.id ?? item?.product_id ?? item?.document_id ?? item?.blob_path ?? item?.name ?? ""}`;
	}
	if (category === "Pages") {
		return `Pages:${item?.href ?? item?.name ?? ""}`;
	}
	return `${category || "Unknown"}:${item?.id ?? item?.product_id ?? item?.name ?? JSON.stringify(item)}`;
};

const dedupeSearchResults = (items = []) => {
	const seen = new Set();
	return items.filter((item) => {
		const key = getSearchResultKey(item);
		if (!key || seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

const ALLOWED_SEARCH_CATEGORIES = ["Products", "Machines", "Orders", "Pages", "Documents"];

/**
 * Header search box with a categorised live-results dropdown (parts, machines, orders, pages,
 * documents). Part rows show the replacement caption so a hit found through the replacement
 * number explains why it matched.
 *
 * @param {Object} props
 * @param {string} [props.width="100%"] CSS width of the input.
 * @param {string} [props.source] Label for where the box is mounted (analytics / styling).
 * @param {boolean} [props.partsOnly=false] Restrict results to parts.
 * @returns {JSX.Element}
 */
const GlobalSearch = ({ width = "100%", source, partsOnly = false, ...rest }) => {
	const { selectedSite } = useSite();
	const { user, hasPermission, canAddToCart } = useAuth();
	const canShowAddToCart = canAddToCart();
	const { stockImageUrl } = useBrandingAssets();
	const accessiblePages = useAccessiblePages();
	const [searchTerm, setSearchTerm] = useState("");
	const [data, setData] = useState([]); // State to store Products
	const [filteredData, setFilteredData] = useState(data);
	const navigate = useNavigate();
	const [isFocused, setIsFocused] = useState(false);
	const containerRef = useRef(null);
	const [resultsLoading, setResultsLoading] = useState(false);
	const lastTrackedSearchRef = useRef("");
	const { t } = useTranslation();
	const location = useLocation();
	const isAdminRoute = useMemo(
			() => (location.pathname || "").toLowerCase().startsWith("/admin"),
			[location.pathname]
	);		

	// Document View Modal state
	const [documentModalOpen, setDocumentModalOpen] = useState(false);
	const [selectedDocument, setSelectedDocument] = useState(null);

	useEffect(() => {
		if (!searchTerm) {
			setData([]);
			setFilteredData([]);
			setResultsLoading(false);
			return;
		}

		if (searchTerm.length < 2 || !selectedSite) {
			setData([]);
			setFilteredData([]);
			setResultsLoading(false);
			return;
		}

		const search = async () => {
			setResultsLoading(true);
			try {
				const reqBody = {
					site_number: selectedSite,
					isGlobal: isAdminRoute,
					searchTerm: searchTerm, // Use the real-time search term
					...(user?.user_id ? { user_id: user.user_id } : {}),
				};

				// Call your NEW fast search endpoint
				const response = await searchProducts(reqBody);
				if (response.success) {
					// The API now returns a small, pre-filtered list.
					// We also add the static "Pages" results here.
					const pageResults = getMatchingPages(accessiblePages, searchTerm, partsOnly);

					// Add href and image to the dynamic results from the backend
					const apiResults = response.data.map(item => {
						const category = String(item?.category || "").trim().toLowerCase();
						if (category === 'products') {
							const raw = item?.asset_data?.images?.[0];
							const imageKey = item.sub_name || item.name;
							return {
								...item,
								category: "Products",
								href: `/machines/${item.machine_id}/${item.product_id}`,
								image: raw
									? resolveAssetImage(raw, stockImageUrl)
									: (imageKey
										? `${ASSETS_BASE_URL}/ProductImages/TMB/${imageKey}.JPG`
										: stockImageUrl || `${ASSETS_BASE_URL}/stock.png`),
							};
						}
						if (category === 'machines') {
							const raw = item?.asset_data?.images?.[0];
							return {
								...item,
								category: "Machines",
								href: `/machines/${item.machine_id}`,
								image: raw
									? resolveAssetImage(raw, stockImageUrl)
									: `${ASSETS_BASE_URL}/MachineImages/${item.sub_name}.png`
							};
						}
						if (category === 'documents') {
							// 1. Extract and validate document ID
							const docId = item.product_id || item.id;
							if (!docId) {
								console.warn('⚠️ Document missing ID:', item);
							}
							
							// 2. Parse categories - ensure array of objects
							let categories = [];
							if (Array.isArray(item.categories)) {
								categories = item.categories;
							} else if (typeof item.categories === 'string') {
								try {
									const parsed = JSON.parse(item.categories);
									categories = Array.isArray(parsed) ? parsed : [];
								} catch {
									categories = [];
								}
							}
						
							// 3. Parse machine_details - ensure array of objects
							let machineDetails = [];
							if (Array.isArray(item.machine_details)) {
								machineDetails = item.machine_details;
							} else if (typeof item.machine_details === 'string') {
								try {
									const parsed = JSON.parse(item.machine_details);
									machineDetails = Array.isArray(parsed) ? parsed : [];
								} catch {
									machineDetails = [];
								}
							}
							
							// 4. Parse assigned_machines - CRITICAL: Convert to array of strings
							let assignedMachines = [];
							if (Array.isArray(item.assigned_machines)) {
								// Could be numbers [20551382] or strings ["20551382"]
								assignedMachines = item.assigned_machines.map(id => String(id));
							} else if (typeof item.assigned_machines === 'string') {
								// String format like "{20551382}" or "[20551382]"
								try {
									// Try JSON parse first
									const parsed = JSON.parse(item.assigned_machines);
									if (Array.isArray(parsed)) {
										assignedMachines = parsed.map(id => String(id));
									} else {
										// Fallback to parseNumberArray
										const numbers = parseNumberArray(item.assigned_machines);
										assignedMachines = numbers.map(id => String(id));
									}
								} catch {
									// Fallback to parseNumberArray
									const numbers = parseNumberArray(item.assigned_machines);
									assignedMachines = numbers.map(id => String(id));
								}
							}
							
							// 5. Determine attachment_type - normalize case
							let attachmentType = 'file';
							if (item.attachment_type) {
								attachmentType = item.attachment_type.toLowerCase();
							} else if (item.attachment) {
								// Infer from attachment URL
								const ext = item.attachment.split('.').pop().toLowerCase();
								if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
									attachmentType = ext;
								}
							}
							
							// 6. Determine blob_path
							const blobPath = item.blob_path || item.attachment || item.path;
						
							// 7. Determine content_type from file extension
							let contentType = null;
							if (item.attachment || blobPath) {
								const url = item.attachment || blobPath;
								const ext = url.split('.').pop().toLowerCase();
								const typeMap = {
									pdf: 'application/pdf',
									doc: 'application/msword',
									docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
									xls: 'application/vnd.ms-excel',
									xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
									ppt: 'application/vnd.ms-powerpoint',
									pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
									txt: 'text/plain',
									csv: 'text/csv',
									png: 'image/png',
									jpg: 'image/jpeg',
									jpeg: 'image/jpeg',
									gif: 'image/gif',
									html: 'text/html'
								};
								contentType = typeMap[ext] || null;
							}
							
							// 8. Extract category name for DocumentViewModal
							const categoryName = categories.length > 0 
								? categories[0].category_name 
								: item.category || 'uncategorized';
							
							// 9. Build the complete normalized document object
							// This structure MUST match what Documentation API returns
							const normalizedDoc = {
								// Core identification
								id: docId,
								product_id: docId, // Alias for compatibility
								
								// Document metadata
								name: item.name,
								description: item.description || null,
								category: "Documents",              // Keep main category for grouping
								document_category: categoryName, 
								
								// Attachment information
								attachment: item.attachment,
								attachment_type: attachmentType,
								blob_path: blobPath,
								path: blobPath, // Alias for compatibility
								content_type: contentType,
								
								// Relationships
								categories: categories, // Array of category objects
								assigned_machines: assignedMachines, // Array of string IDs
								machine_details: machineDetails, // Array of machine objects
								
								// Site information
								site_id: item.site_id,
								
								// Additional metadata
								document_type: item.document_type || null,
								visibility_scope: item.visibility_scope || 'ALL',
								visibility_status: item.visibility_status || 'PUBLIC',
								
								// Timestamps
								uploaded_at: item.uploaded_at || null,
								updated_at: item.updated_at || null,
								
								// Optional caching fields
								sas_url: item.sas_url || null,
								sas_expires_at: item.sas_expires_at || null,
								
								// Keep original data for debugging
								_originalItem: item
							};
		
							return normalizedDoc;
						}
						return item;
					}).filter((item) => {
						if (getResultCategory(item) === "Orders") {
							return canAccessOrderHistory(hasPermission);
						}
						return true;
					});

					// Combine API results with page results
					const allResults = dedupeSearchResults([...apiResults, ...pageResults]);

					const filteredByCategory = allResults.filter((item) => {
						const cat = String(item?.category || "").trim().toLowerCase();
						return ALLOWED_SEARCH_CATEGORIES.some((allowed) => allowed.toLowerCase() === cat);
					});
					const filteredResults = partsOnly
						? filteredByCategory.filter((item) => {
							const cat = String(item?.category || "").trim().toLowerCase();
							return cat === "products" || cat === "machines";
						})
						: filteredByCategory;
					setData(filteredResults);
					setFilteredData(filteredResults);
					const trimmedSearchTerm = searchTerm.trim();
					const resultCount = filteredResults.length;
					const trackingKey = `${trimmedSearchTerm.toLowerCase()}|${selectedSite}|${partsOnly}|${resultCount}`;
					if (trimmedSearchTerm && lastTrackedSearchRef.current !== trackingKey) {
						lastTrackedSearchRef.current = trackingKey;
						trackEvent("search_performed", {
							search_term: trimmedSearchTerm,
							search_source: partsOnly ? "parts_search" : "global_search",
							page_name: document.title || "Global Search",
							page_path: `${location.pathname}${location.search || ""}`,
							result_count: resultCount,
							has_results: resultCount > 0,
							no_results: resultCount === 0,
						});
					}
					}
			} catch (error) {
				console.error("Error during search", error);
			} finally {
				setResultsLoading(false);
			}
		};

		// Use a timeout to debounce the search, so we don't spam the API on every keystroke
		const debounceTimeout = setTimeout(() => {
			search();
		}, 300); // 300ms delay

		// Cleanup function to cancel the timeout if the user keeps typing
		return () => clearTimeout(debounceTimeout);

		}, [searchTerm, selectedSite, stockImageUrl, user?.user_id, partsOnly, accessiblePages, isAdminRoute, location.pathname, location.search]); // This effect now runs only when the search term or site changes

	useEffect(() => {
		const clearGlobalSearch = () => {
			setSearchTerm("");
			setData([]);
			setFilteredData([]);
			setResultsLoading(false);
			setIsFocused(false);
		};

		window.addEventListener("global-search:clear", clearGlobalSearch);
		return () => window.removeEventListener("global-search:clear", clearGlobalSearch);
	}, []);

	const handleSearchChange = (event) => {
		const value = event.target.value;
		setSearchTerm(value);
		setResultsLoading(true);

		// Filter the data based on the search term
	};

	useEffect(() => {
		if (searchTerm === "") {
			setFilteredData(data);
		} else {
			if (searchTerm.length >= 2) {
				const term = searchTerm.toLowerCase();

				const filtered = data.filter((item) => {
					const nameMatch = (item.name || "").toLowerCase().includes(term);
					const categoryMatch = (item.category || "").toLowerCase().includes(term);
					const subNameMatch = (item.sub_name || "").toLowerCase().includes(term);

					// Extra matching for documents: search in machine numbers and names
					if (String(item?.category || "").trim().toLowerCase() === "documents") {
						const machineDetails = parseJsonArray(item.machine_details);
						const machineMatch = machineDetails.some((m) => {
							const mNum = (m?.machine_number || "").toLowerCase();
							const mName = (m?.machine_name || "").toLowerCase();
							return mNum.includes(term) || mName.includes(term);
						});

						return nameMatch || categoryMatch || subNameMatch || machineMatch;
					}

					// Extra matching for machines/products: include machine_number if present
					const machineNumberMatch = (item.machine_number || "").toLowerCase().includes(term);

					return nameMatch || categoryMatch || subNameMatch || machineNumberMatch;
				});

				setFilteredData(filtered);
			}
		}
	}, [searchTerm, data]);

	const handleItemClick = (item) => {
		setIsFocused(false);
		const category = String(item?.category || "").trim().toLowerCase();
		// Documents don't have an href route; open in modal instead of navigating.
		if (category === "documents" || item?.attachment || item?.blob_path || item?.path) {
			setSelectedDocument(item);
			setDocumentModalOpen(true);
			return;
		}
		if (category === "orders") {
			const params = new URLSearchParams();
			const orderId = item?.order_id ?? item?.product_id;
			const orderNumber = item?.order_number ?? item?.name;
			if (orderId) params.set("open_id", orderId);
			if (orderNumber) params.set("open_number", orderNumber);
			navigate(`/order-history${params.toString() ? `?${params.toString()}` : ""}`);
			return;
		}
		if (item?.href) {
			navigate(`${item.href}`);
			return;
		}
		console.warn("GlobalSearch: clicked item without href:", item);
		// You can perform any action on click here, like navigating or showing details
	};

	const handleDocumentCategoryClick = (categoryName) => {
		if (!categoryName) return;
		setIsFocused(false);
		navigate(`/documentation?category=${encodeURIComponent(categoryName)}`);
	};

	const triggerFullSearch = () => {
		if (!searchTerm || searchTerm.length < 1) return;
		setIsFocused(false);
		const params = new URLSearchParams();
		params.set("q", searchTerm);
		if (partsOnly) params.set("partsonly", "true");
		navigate(`/search-results?${params.toString()}`);
	};

	const handleImgError = (e) => {
		const defaultImage = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
		e.target.onerror = null; // Prevent infinite loop
		e.target.src = defaultImage; // Fallback to default image
	};

	const handleCloseDocumentModal = () => {
		setDocumentModalOpen(false);
		setSelectedDocument(null);
	};

	const clearSearch = () => {
		setSearchTerm("");
		setData([]);
		setFilteredData([]);
		setResultsLoading(false);
		setIsFocused(false);
	};

	// Group filtered data by category
	const documentsList = useMemo(() => filteredData.filter(r => r.category === "Documents"), [filteredData]);

	const documentCategories = useMemo(() => {
		if (!documentsList.length) return [];
		const map = new Map();
		const fallbackName = t('UI_TEXT.UNCATEGORIZED') || 'Uncategorized';
		documentsList.forEach((doc) => {
			const categories = Array.isArray(doc.categories) ? doc.categories : [];
			if (!categories.length) {
				const entry = map.get(fallbackName) || { name: fallbackName, count: 0 };
				entry.count += 1;
				map.set(fallbackName, entry);
				return;
			}
			categories.forEach((cat) => {
				const name = (cat?.category_name || fallbackName).trim();
				if (!name) return;
				const entry = map.get(name) || { name, count: 0 };
				entry.count += 1;
				map.set(name, entry);
			});
		});
		return Array.from(map.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
	}, [documentsList, t]);

	const categorizedData = {
		Products: filteredData.filter((item) => String(item?.category || "").trim().toLowerCase() === "products"),
		Machines: filteredData.filter((item) => String(item?.category || "").trim().toLowerCase() === "machines"),
		Orders: canAccessOrderHistory(hasPermission)
			? filteredData.filter((item) => String(item?.category || "").trim().toLowerCase() === "orders")
			: [],
		Pages: filteredData.filter((item) => String(item?.category || "").trim().toLowerCase() === "pages"),
		Documents: filteredData.filter((item) => String(item?.category || "").trim().toLowerCase() === "documents"),
	};

	useEffect(() => {
		if (filteredData.length >= 0) {
			setResultsLoading(false);
		}
	}, [filteredData]);

	useEffect(() => {
		function handleClickOutside(event) {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setIsFocused(false);
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<>
			<div className="search-container" style={{ width }} ref={containerRef} data-tour="main-page-search" {...rest}>
				<TextField
					variant="outlined"
					placeholder={canShowAddToCart ? t('PLACEHOLDER.SEARCHS') : t('PLACEHOLDER.SEARCHS_NO_ORDERS')}
					fullWidth
					value={searchTerm}
					onChange={handleSearchChange}
					onFocus={() => setIsFocused(true)}
					size="small"
					slotProps={{
						input: {
							endAdornment: (
								<Box display="flex">
									{searchTerm ? (
										<IconButton
											marginRight="-2px"
											onClick={clearSearch}
											aria-label="Clear search"
										>
											<CloseIcon fontSize="small" />
										</IconButton>
									) : null}
									<IconButton marginRight="-2px" onClick={triggerFullSearch}>
										<SearchIcon />
									</IconButton>
								</Box>
							),
						},
					}}
					sx={{
						"& .MuiInputBase-root": {
							paddingRight: "2px", // Ensure there's no extra padding in the input field itself
						},
						"& .MuiOutlinedInput-root": {
							borderRadius: "40px",
							backgroundColor: "#fff",
						},
						"& .MuiInputBase-input::placeholder": {
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						},
						width: "100%",
					}}
					onKeyDown={(e) => { if (e.key === 'Enter') { triggerFullSearch(); } }}
				/>

				{/* The dropdown-style container for search results */}
				{source !== 'page' && isFocused && searchTerm.length >= 2 && (
					<Paper className="search-results">
						{resultsLoading ? (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									width: "100%",
									padding: "10px",
									flexDirection: "row",
									columnGap: "10px",
								}}>
								<CircularProgress color="inherit" size="25px" />
								<Typography variant="body1" fontSize="11px">
									{t('UI_TEXT.LOADING')}
								</Typography>
							</Box>
						) : (
							<List>
								{/* Display Pages Results */}
								{categorizedData.Pages.length > 0 && (
									<>
										<Typography variant="h6" className="category-header">
											{t('UI_TEXT.PAGES')}
										</Typography>
										{categorizedData.Pages.map((item, index) => (
											<ListItem
												key={index}
												className="result-item"
												onClick={() => handleItemClick(item)}>
												<img
													src={item.image}
													alt={item.name}
													onError={handleImgError}
													className="result-image"
												/>
												<div className="result-info">
													<ListItemText primary={item.name} />
													<div className="category-text">{item.sub_name}</div>
												</div>
											</ListItem>
										))}
									</>
								)}

								{/* Display Products Results */}
								{categorizedData.Products.length > 0 && (
									<>
										<Typography variant="h6" className="category-header">
											{t('UI_TEXT.PARTS')}
										</Typography>
										{categorizedData.Products.map((item, index) => (
											<ListItem
												key={index}
												className="result-item"
												onClick={() => handleItemClick(item)}>
												<img
													src={item.image}
													alt={item.name}
													className="result-image"
													onError={handleImgError}
												/>
												<div className="result-info">
													<ListItemText primary={item.name} />
													<div className="category-text">{t('UI_TEXT.PART')} {item.sub_name} {item.machine_number ? ` | ${t('UI_TEXT.MACHINE')} ${item.machine_number}` : ''}</div>
													<ReplacementPartNotice product={item} component="div" />
												</div>
											</ListItem>
										))}
									</>
								)}

								{/* Display Machines Results */}
								{categorizedData.Machines.length > 0 && (
									<>
										<Typography variant="h6" className="category-header">
											{t('UI_TEXT.MACHINES')}
										</Typography>
										{categorizedData.Machines.map((item, index) => (
											<ListItem
												key={index}
												className="result-item"
												onClick={() => handleItemClick(item)}>
												<img
													src={item.image}
													alt={item.name}
													className="result-image"
													onError={handleImgError}
												/>
												<div className="result-info">
													<ListItemText primary={item.name} />
													<div className="category-text">{t('UI_TEXT.MACHINE')} {item.sub_name}</div>
												</div>
											</ListItem>
										))}
									</>
								)}

								{categorizedData.Orders.length > 0 && (
									<>
										<Typography variant="h6" className="category-header">
											{t('TITLES.ORDER', { defaultValue: 'Orders' })}
										</Typography>
										{categorizedData.Orders.map((item, index) => (
											<ListItem
												key={index}
												className="result-item"
												onClick={() => handleItemClick(item)}>
												<img
													src={stockImageUrl || `${ASSETS_BASE_URL}/stock.png`}
													alt={item.name}
													className="result-image"
													onError={handleImgError}
												/>
												<ResultText primary={item.name} secondary={`${t('UI_TEXT.MACHINE')} ${item.sub_name}`} />
											</ListItem>
										))}
									</>
								)}

								{categorizedData.Documents.length > 0 && (
									<>
										<Typography variant="h6" className="category-header">
											{t('UI_TEXT.DOCUMENTS')}
										</Typography>
										{categorizedData.Documents.map((item, index) => (
											<ListItem
												key={`doc-${item.product_id}-${index}`}
												className="result-item"
												onClick={() => handleItemClick(item)}>
												<DocCardImage
													type={item.attachment_type}
													path={item.attachment}
												/>
												<div className="result-info">
													<ListItemText primary={item.name} />
													<div className="category-text">{buildDocSubtitle(item, t)}</div>
												</div>
											</ListItem>
										))}
									</>
								)}

								{documentCategories.length > 0 && (
									<>
										<Typography variant="h6" className="category-header">
											{t('UI_TEXT.DOCUMENT_CATEGORIES')}
										</Typography>
										{documentCategories.map((item) => (
											<ListItem
												key={`doc-cat-${item.name}`}
												className="result-item"
												onClick={() => handleDocumentCategoryClick(item.name)}>
												<div className="result-info">
													<ListItemText primary={item.name} />
													<div className="category-text">
														{item.count} {t('UI_TEXT.DOCUMENTS')}
													</div>
												</div>
											</ListItem>
										))}
									</>
								)}

								{/* Display message when no results are found */}
								{filteredData.length === 0 && (
									<ListItem className="no-results">
										<ListItemText primary="No results found" />
									</ListItem>
								)}
							</List>
						)}
					</Paper>
				)}
			</div>

			{/* Document View Modal */}
			{selectedDocument && (
				<DocumentViewModal
					open={documentModalOpen}
					setOpen={setDocumentModalOpen}
					document={selectedDocument}
					onClose={handleCloseDocumentModal}
					onEdit={null} // Add onEdit handler if needed
					onDelete={null} // Add onDelete handler if needed
				/>
			)}
		</>
	);
};

export default GlobalSearch;
